import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Quotes, GoogleLogo, ArrowUpRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/site";

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star key={i} size={16} weight="fill" className={i < n ? "text-brand-orange" : "text-brand-sand"} />
      ))}
    </div>
  );
}

function ReviewCard({ author, text, rating, relative_time, profile_photo, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.25, 1, 0.5, 1] }}
      className="h-full rounded-2xl bg-white border border-brand-sand p-8 flex flex-col"
      data-testid={`google-review-${index}`}
    >
      <div className="flex items-center justify-between">
        <Quotes size={30} weight="fill" className="text-brand-teal/30" />
        <GoogleLogo size={22} weight="bold" className="text-[#4285F4]" />
      </div>
      <p className="mt-4 text-[15px] text-[#4a477b] font-body leading-relaxed flex-1 line-clamp-6">{text || "Highly recommended!"}</p>
      <div className="mt-6 flex items-center gap-3">
        {profile_photo ? (
          <img src={profile_photo} alt={author} referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <span className="h-10 w-10 rounded-full bg-brand-teal/15 grid place-items-center font-heading text-brand-teal">{(author || "G")[0]}</span>
        )}
        <div className="flex-1">
          <p className="font-heading text-base text-brand-navy leading-tight">{author}</p>
          {relative_time && <p className="text-xs text-[#8B89A4]">{relative_time}</p>}
        </div>
        <Stars n={rating} />
      </div>
    </motion.div>
  );
}

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reviews")
      .then((r) => setData(r.data))
      .catch(() => setData({ configured: false, reviews: [] }))
      .finally(() => setLoading(false));
  }, []);

  const live = data?.configured && Array.isArray(data.reviews) && data.reviews.length > 0;
  const items = live
    ? data.reviews
    : TESTIMONIALS.map((t) => ({ author: t.name, text: t.text, rating: t.rating, relative_time: t.trip }));

  return (
    <section className="bg-brand-sand py-24 md:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="font-accent text-xs font-bold uppercase tracking-[0.25em] text-brand-orange mb-4">
              {live ? "Google Reviews" : "Loved by travellers"}
            </p>
            <h2 className="text-4xl sm:text-5xl font-heading font-medium text-brand-navy leading-tight">Stories from the road</h2>
            {live && (
              <div className="mt-5 flex items-center gap-3">
                <GoogleLogo size={26} weight="bold" className="text-[#4285F4]" />
                <span className="font-heading text-2xl text-brand-navy">{Number(data.rating || 0).toFixed(1)}</span>
                <Stars n={Math.round(data.rating || 5)} />
                <span className="text-sm text-[#8B89A4]">({data.total} Google reviews)</span>
              </div>
            )}
          </div>
          {live && data.google_maps_uri && (
            <a
              href={data.google_maps_uri}
              target="_blank"
              rel="noreferrer"
              data-testid="see-all-reviews"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-sand px-6 py-3 text-sm font-semibold text-brand-navy transition-colors hover:border-brand-teal hover:text-brand-teal shrink-0"
            >
              See all reviews on Google <ArrowUpRight size={18} weight="bold" />
            </a>
          )}
        </Reveal>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="h-56 rounded-2xl bg-white/60 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.slice(0, 6).map((r, i) => <ReviewCard key={i} index={i} {...r} />)}
          </div>
        )}
      </div>
    </section>
  );
}
